import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { toast } from 'sonner';
import axios from 'axios';
import { Mail, MessageSquare, HelpCircle, ChevronRight, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HelpCenter = () => {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const faqs = [
    {
      question: "How do I purchase a course?",
      answer: "Browse our course catalog, select a course you're interested in, and click 'Buy This Course'. You'll be redirected to our secure checkout powered by Stripe. After payment, you'll have lifetime access to the course."
    },
    {
      question: "Can I get a refund?",
      answer: "Due to the digital nature of our courses, all sales are final. Please review the course description, preview lessons, and curriculum before purchasing. See our Refund Policy for more details."
    },
    {
      question: "How do I become an instructor?",
      answer: "Register for an account and select 'Teach' during signup. Then subscribe to an instructor plan (Starter, Pro, or Unlimited) to start creating and publishing courses."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards (Visa, MasterCard, American Express) through our secure Stripe payment system."
    },
    {
      question: "How do I access my purchased courses?",
      answer: "After purchase, go to your Dashboard and click 'Continue Learning' on any course. All your purchased courses are available there with lifetime access."
    },
    {
      question: "Can I download course content?",
      answer: "Course videos are streamed for security. However, downloadable resources (PDFs, code files) provided by instructors can be downloaded."
    },
    {
      question: "How does the referral program work?",
      answer: "Share your unique referral link with friends. When they sign up and make their first purchase, they get 20% off and you earn $10 in credits!"
    },
    {
      question: "How do instructors get paid?",
      answer: "Instructors keep 80% of each course sale. Earnings accumulate in your dashboard and you can request a payout once you reach the $10 minimum."
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="help-center">
      <Navbar />

      {/* Hero */}
      <div className="pt-24 pb-12 bg-gradient-to-br from-primary/5 to-purple-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <HelpCircle className="w-16 h-16 text-primary mx-auto mb-4" />
          <h1 className="font-heading text-4xl font-bold text-slate-900 mb-4">Help Center</h1>
          <p className="text-lg text-slate-600">Find answers to common questions or contact us directly</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* FAQ Section */}
          <div className="lg:col-span-2">
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-6">
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="space-y-3">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`faq-${index}`}
                  className="bg-white rounded-lg border border-slate-200 px-4"
                >
                  <AccordionTrigger className="text-left font-medium text-slate-900 hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            {/* Quick Links */}
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link to="/knowledge-base" className="p-4 bg-white rounded-lg border border-slate-200 hover:border-primary transition-colors">
                <p className="font-medium text-slate-900">Knowledge Base</p>
                <p className="text-xs text-slate-500 mt-1">Tutorials & Guides</p>
                <ChevronRight className="w-4 h-4 text-slate-400 mt-1" />
              </Link>
              <Link to="/privacy" className="p-4 bg-white rounded-lg border border-slate-200 hover:border-primary transition-colors">
                <p className="font-medium text-slate-900">Privacy Policy</p>
                <ChevronRight className="w-4 h-4 text-slate-400 mt-1" />
              </Link>
              <Link to="/terms" className="p-4 bg-white rounded-lg border border-slate-200 hover:border-primary transition-colors">
                <p className="font-medium text-slate-900">Terms & Conditions</p>
                <ChevronRight className="w-4 h-4 text-slate-400 mt-1" />
              </Link>
              <Link to="/refund-policy" className="p-4 bg-white rounded-lg border border-slate-200 hover:border-primary transition-colors">
                <p className="font-medium text-slate-900">Refund Policy</p>
                <ChevronRight className="w-4 h-4 text-slate-400 mt-1" />
              </Link>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-1">
            <Card className="border-slate-200 sticky top-24">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-primary" />
                  Contact Us
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Input
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <Input
                      type="email"
                      placeholder="Your Email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      data-testid="contact-email"
                    />
                  </div>
                  <div>
                    <Input
                      placeholder="Subject"
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      data-testid="contact-subject"
                    />
                  </div>
                  <div>
                    <Textarea
                      placeholder="Your Message"
                      rows={4}
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      data-testid="contact-message"
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={sending} data-testid="contact-submit">
                    {sending ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>

                <div className="mt-6 pt-6 border-t border-slate-200 text-center">
                  <p className="text-sm text-slate-500">Or email us directly at</p>
                  <a href="mailto:info@aiskillflowpro.com" className="text-primary font-medium hover:underline">
                    info@aiskillflowpro.com
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
